import {createStyles, Flex} from '@mantine/core';
import classNames from 'classnames';
import React from 'react';

import {AntibodyComponentType} from 'antibodies/AntibodyConstants';
import AaChainVis from 'antibodies/nested-data-creation/antibody-component-visualization/AaChainVis';
import AaDomainVis from 'antibodies/nested-data-creation/antibody-component-visualization/AaDomainVis';
import DnaDomainVis from 'antibodies/nested-data-creation/antibody-component-visualization/DnaDomainVis';
import {assert} from 'Asserts';

const ANTIBODY_COMPONENT_SVG_PADDING = 4;

const COMPONENT_TYPE_TO_RENDERED_SVG: Partial<Record<AntibodyComponentType, () => JSX.Element>> = {
  [AntibodyComponentType.AA_DOMAIN]: AaDomainVis,
  [AntibodyComponentType.DNA_DOMAIN]: DnaDomainVis,
  [AntibodyComponentType.AA_CHAIN]: AaChainVis,
};

const useStyles = createStyles((theme) => ({
  containerBackground: {
    backgroundColor: theme.colors.gray[0],
  },
  containerBorder: {
    border: `1px solid ${theme.colors.gray[1]}`,
    borderRadius: 4,
  },
  componentSvg: {
    margin: 'auto',
  },
}));

interface AntibodyComponentSvgProps {
  componentType: AntibodyComponentType;
  size: number;
  useDefaultBackground?: boolean;
  useDefaultBorder?: boolean;
}

/**
 * Renders an SVG representing a component of an antibody on a gray background.
 */
export default function AntibodyComponentSvg({
  componentType,
  size,
  useDefaultBackground = true,
  useDefaultBorder = true,
}: AntibodyComponentSvgProps): JSX.Element {
  const {classes} = useStyles();

  assert(
    COMPONENT_TYPE_TO_RENDERED_SVG.hasOwnProperty(componentType),
    `Unknown component type: ${componentType}`
  );
  return (
    <Flex
      className={classNames(
        {[classes.containerBackground]: useDefaultBackground},
        {[classes.containerBorder]: useDefaultBorder}
      )}
      style={{width: `${size}px`, height: `${size}px`}}
    >
      <svg
        className={classes.componentSvg}
        height={size - ANTIBODY_COMPONENT_SVG_PADDING * 2}
        width={size - ANTIBODY_COMPONENT_SVG_PADDING * 2}
        // All component visualizations are expected to be exported in a 72x62 viewbox.
        viewBox="0 0 72 62"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
      >
        {COMPONENT_TYPE_TO_RENDERED_SVG[componentType]!()}
      </svg>
    </Flex>
  );
}
